import {ehrService} from '../../services/ehrService';
import {patientService} from '../../services/patientService';

const CACHE_TTL_MS = 5 * 60 * 1000;

interface CacheEntry {
  expiresAt: number;
  value: any;
}

type RecordCache = Map<string, CacheEntry>;
type PendingRecordMap = Map<string, Promise<any>>;

const localPatientCache: RecordCache = new Map();
const linkedLocalPatientCache: RecordCache = new Map();
const ehrPatientCache: RecordCache = new Map();

const localPatientPending: PendingRecordMap = new Map();
const linkedLocalPatientPending: PendingRecordMap = new Map();
const ehrPatientPending: PendingRecordMap = new Map();

const cleanId = (value: any) => String(value ?? '').trim();

const uniqueIds = (ids: any[] = []) => [
  ...new Set(ids.map(cleanId).filter(Boolean)),
];

const scopedKey = (
  id: any,
  {companyId = '', facilityId = ''}: Record<string, any> = {},
) => [cleanId(companyId), cleanId(facilityId), cleanId(id)].join('\u0001');

const ehrScopedKey = (companyId: any, id: any) =>
  [cleanId(companyId), cleanId(id)].join('\u0001');

const readCacheValue = (cache: RecordCache, key: string) => {
  const cached = cache.get(key);
  if (!cached) return {hit: false, value: null};
  if (cached.expiresAt <= Date.now()) {
    cache.delete(key);
    return {hit: false, value: null};
  }
  return {hit: true, value: cached.value};
};

const writeCacheValue = (cache: RecordCache, key: string, value: any) => {
  cache.set(key, {
    expiresAt: Date.now() + CACHE_TTL_MS,
    value: value || null,
  });
};

const fetchCachedRecords = async ({
  cache,
  fetchMissing,
  ids,
  keyForId = (id: any) => cleanId(id),
  pending,
}: {
  cache: RecordCache;
  fetchMissing: (ids: string[]) => Promise<Record<string, any>>;
  ids: any[];
  keyForId?: (id: string) => string;
  pending: PendingRecordMap;
}) => {
  const result: Record<string, any> = {};
  const missingIds: string[] = [];
  const pendingLookups: Array<Promise<[string, any]>> = [];

  uniqueIds(ids).forEach(id => {
    const key = keyForId(id);
    const cached = readCacheValue(cache, key);
    if (cached.hit) {
      if (cached.value) result[id] = cached.value;
      return;
    }

    const pendingRecord = pending.get(key);
    if (pendingRecord) {
      pendingLookups.push(pendingRecord.then(value => [id, value]));
      return;
    }

    missingIds.push(id);
  });

  let fetched: Record<string, any> = {};
  if (missingIds.length) {
    const request: Promise<Record<string, any>> = fetchMissing(missingIds)
      .then(map => map || {})
      .catch(() => ({}) as Record<string, any>);

    missingIds.forEach(id => {
      const key = keyForId(id);
      pending.set(
        key,
        request.then(map => map[id] || null).finally(() => pending.delete(key)),
      );
    });

    fetched = await request;
    missingIds.forEach(id => {
      const value = fetched[id] || null;
      writeCacheValue(cache, keyForId(id), value);
      if (value) result[id] = value;
    });
  }

  if (pendingLookups.length) {
    const resolvedPending = await Promise.all(pendingLookups);
    resolvedPending.forEach(([id, value]) => {
      if (value) result[id] = value;
    });
  }

  return result;
};

export const clearPlannerPatientHydrationCache = () => {
  localPatientCache.clear();
  linkedLocalPatientCache.clear();
  ehrPatientCache.clear();
  localPatientPending.clear();
  linkedLocalPatientPending.clear();
  ehrPatientPending.clear();
};

export const fetchPlannerLocalPatientsByIds = (ids: any[] = []) =>
  fetchCachedRecords({
    cache: localPatientCache,
    fetchMissing: missingIds => patientService.fetchPatientsByIds(missingIds),
    ids,
    pending: localPatientPending,
  });

export const fetchPlannerLinkedLocalPatientsByEhrIds = (
  ids: any[] = [],
  options: Record<string, any> = {},
) =>
  fetchCachedRecords({
    cache: linkedLocalPatientCache,
    fetchMissing: missingIds =>
      patientService.fetchPatientsByLinkedEhrIds(missingIds, options),
    ids,
    keyForId: id => scopedKey(id, options),
    pending: linkedLocalPatientPending,
  });

export const resolvePlannerEhrPatients = (
  companyId: any,
  ids: any[] = [],
  options: Record<string, any> = {},
) => {
  const hasOptions = Object.keys(options || {}).length > 0;
  return fetchCachedRecords({
    cache: ehrPatientCache,
    fetchMissing: missingIds =>
      hasOptions
        ? ehrService.resolveEhrPatients(companyId, missingIds, options)
        : ehrService.resolveEhrPatients(companyId, missingIds),
    ids,
    keyForId: id => ehrScopedKey(companyId, id),
    pending: ehrPatientPending,
  });
};
